import { useState, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useNavigate } from 'react-router-dom';
import {
  Phone, Mail, MapPin, Calendar, FileText, User, Pencil, Printer,
  Briefcase, Wrench, Hash, StickyNote, Landmark, Clock, Send, Link as LinkIcon, Globe, Ban, Trash2,
  PenLine, Smartphone,
} from 'lucide-react';
import { QuickViewSheetLayout } from '@/components/ui/QuickViewSheetLayout';
import { SaveStatusIndicator } from '@/components/ui/SaveStatusIndicator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useInvoice, useMarkInvoiceSent } from '@/hooks/useInvoices';
import { useLinkedCustomer } from '@/hooks/useCustomers';
import { CustomerLink } from '@/components/customers/CustomerLink';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { resolveTaxConfig, formatTaxLine } from '@/utils/tax';
import { useSavingIndicator } from '@/hooks/useSavingIndicator';
import { cn } from '@/lib/utils';
import { CANCELLABLE_INVOICE_STATUSES, canDeleteInvoice, type InvoiceLineItem } from '@/types/invoice';
import { useVisibility } from '@/hooks/useVisibility';
import { InvoiceActionConfirmDialog, type InvoiceConfirmAction } from './InvoiceActionConfirmDialog';
import { MarkInvoicePaidDialog } from './MarkInvoicePaidDialog';
import { EditInvoiceDialog } from '@/components/invoices/EditInvoiceDialog';
import { SendForPaymentDialog, copyPaymentLink } from '@/components/invoices/SendForPaymentDialog';
import { EmailInvoiceDialog, canEmailInvoice } from '@/components/invoices/EmailInvoiceDialog';
import { InvoicePreview } from './InvoicePreview';
import { InvoiceSignatureDialog } from './InvoiceSignatureDialog';
import { useTechUsers } from '@/hooks/useReferenceData';
import { documentPageStyle } from '@/components/documents/print';

interface InvoiceQuickViewSheetProps {
  invoiceId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
}

const STATUS_BADGE_VARIANTS: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground border-transparent',
  sent: 'bg-info/10 text-info border-transparent',
  paid: 'bg-success/10 text-success border-transparent',
  overdue: 'bg-destructive/10 text-destructive border-transparent',
  cancelled: 'bg-muted text-muted-foreground border-transparent',
  refunded: 'bg-muted text-muted-foreground border-transparent',
};

/** Square invoice states after which the paid status is final and no longer synced. */
const PROVIDER_TERMINAL_STATUSES = ['PAID', 'REFUNDED', 'CANCELED'];

function formatProviderStatus(status: string | null | undefined): string {
  return (status ?? 'sent').toLowerCase().replace(/_/g, ' ');
}

function Fact({ icon: Icon, label, children, className }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-start gap-2 text-sm', className)}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="text-muted-foreground">{label}</span>
      <span className="ml-auto text-right">{children}</span>
    </div>
  );
}

export function InvoiceQuickViewSheet({
  invoiceId,
  open,
  onOpenChange,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
}: InvoiceQuickViewSheetProps) {
  const navigate = useNavigate();
  const { data: invoice, isLoading, error } = useInvoice(invoiceId);
  // Falls back to a phone match for invoices that never got a customer_id.
  const { data: linkedCustomer } = useLinkedCustomer(invoice);
  const { data: company } = useCompanyProfile();
  const { formatMoney, formatShortDate, locale } = useCompanyLocale();
  const liveTax = useCompanyTax();
  const { canSeeCompanyFinancials } = useVisibility();
  const tax = resolveTaxConfig(invoice, liveTax);

  const markSent = useMarkInvoiceSent();

  const sentSaving = useSavingIndicator();

  const [showEdit, setShowEdit] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [showMarkPaid, setShowMarkPaid] = useState(false);
  const [confirmAction, setConfirmAction] = useState<InvoiceConfirmAction | null>(null);
  // Invoices close_job created from the app carry the technician's user id in created_by.
  const { data: techUsers } = useTechUsers();
  const closedFromApp = !!invoice?.created_by && !!techUsers?.has(invoice.created_by);

  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: invoice?.invoice_number || 'Invoice',
    pageStyle: documentPageStyle(locale.paper),
  });

  const handleMarkSent = async () => {
    if (!invoiceId) return;
    try {
      await sentSaving.trackMutation(markSent.mutateAsync(invoiceId));
    } catch {
      // Error handled by hook
    }
  };

  const canMarkSent = invoice?.status === 'draft';
  const canCancel = !!invoice && CANCELLABLE_INVOICE_STATUSES.includes(invoice.status);
  const canDelete = !!invoice && canDeleteInvoice(invoice, canSeeCompanyFinancials);
  const canMarkPaid = invoice?.status === 'sent' || invoice?.status === 'overdue';
  // Paid / refunded invoices open in the editor too, but with the total locked
  // (the dialog blocks saving unless the line items add back up to it).
  const canEdit = invoice && invoice.status !== 'cancelled';
  // Once published to Square, paid status comes from the webhook until the
  // provider reaches a terminal state.
  const paidSyncedFromProvider =
    !!invoice?.provider_invoice_id &&
    !PROVIDER_TERMINAL_STATUSES.includes((invoice.provider_invoice_status ?? '').toUpperCase());
  const canSendForPayment =
    !!invoice &&
    ['draft', 'sent', 'overdue'].includes(invoice.status) &&
    !invoice.provider_invoice_id;
  const paymentUrl = invoice?.payment_url ?? null;
  const amountRefunded = Number(invoice?.amount_refunded ?? 0);

  const lineItems: InvoiceLineItem[] = invoice?.line_items || [];
  const outstanding = invoice ? Number(invoice.amount_outstanding ?? invoice.total_amount - invoice.amount_paid) : 0;
  const terms = invoice?.terms || company?.invoice_terms || null;

  return (
    <>
      <QuickViewSheetLayout
        open={open}
        onOpenChange={onOpenChange}
        size="xl"
        title={invoice?.invoice_number || 'Invoice'}
        subtitle={invoice ? `${invoice.customer_name}${invoice.job?.job_number ? ` · Job ${invoice.job.job_number}` : ''}` : undefined}
        isLoading={isLoading}
        error={error as Error | null}
        isEmpty={!invoice && !isLoading && !error}
        onPrevious={onPrevious}
        onNext={onNext}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        badges={
          invoice && (
            <Badge
              variant="outline"
              className={cn('capitalize', STATUS_BADGE_VARIANTS[invoice.status])}
            >
              {invoice.status}
            </Badge>
          )
        }
        actions={
          invoice && (
            <div className="flex flex-wrap items-center gap-3">
              <Button size="sm" variant="outline" onClick={() => handlePrint()} className="gap-1">
                <Printer className="h-3.5 w-3.5" />
                PDF
              </Button>
              {invoice.signature_path && (
                <Button size="sm" variant="outline" onClick={() => setShowSignature(true)} className="gap-1">
                  <PenLine className="h-3.5 w-3.5" />
                  View signature
                </Button>
              )}
              {canEdit && (
                <Button size="sm" variant="outline" onClick={() => setShowEdit(true)}>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" />
                  Edit
                </Button>
              )}
              {canEmailInvoice(invoice) && (
                <Button size="sm" variant="outline" onClick={() => setShowEmail(true)}>
                  <Mail className="mr-1.5 h-3.5 w-3.5" />
                  Email invoice
                </Button>
              )}
              {canSendForPayment && (
                <Button size="sm" variant="outline" onClick={() => setShowSend(true)}>
                  <Send className="mr-1.5 h-3.5 w-3.5" />
                  Send for payment
                </Button>
              )}
              {paymentUrl && (
                <Button size="sm" variant="outline" onClick={() => void copyPaymentLink(paymentUrl)}>
                  <LinkIcon className="mr-1.5 h-3.5 w-3.5" />
                  Copy pay link
                </Button>
              )}
              {canMarkSent && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleMarkSent}
                    disabled={sentSaving.status === 'saving'}
                  >
                    Mark as Sent
                  </Button>
                  <SaveStatusIndicator status={sentSaving.status} error={sentSaving.error} />
                </div>
              )}
              {canMarkPaid && (
                <Button
                  size="sm"
                  onClick={() => setShowMarkPaid(true)}
                  disabled={paidSyncedFromProvider}
                  title={paidSyncedFromProvider ? 'Paid status is synced from Square' : undefined}
                >
                  Mark as Paid
                </Button>
              )}
              {canCancel && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmAction('cancel')}
                  className="gap-1 text-warning hover:text-warning"
                >
                  <Ban className="h-3.5 w-3.5" />
                  Cancel
                </Button>
              )}
              {canDelete && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmAction('delete')}
                  className="gap-1 text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </Button>
              )}
            </div>
          )
        }
      >
        {invoice && (
          <div className="space-y-4">
            {/* Headline amount */}
            <div className="rounded-lg bg-primary/10 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-primary">
                {invoice.status === 'refunded'
                  ? 'Refunded'
                  : invoice.status === 'paid'
                    ? 'Paid in full'
                    : outstanding > 0 && invoice.amount_paid > 0
                      ? 'Balance due'
                      : 'Total'}
              </p>
              <p className="mt-0.5 text-2xl font-semibold tabular-nums tracking-tight">
                {formatMoney(
                  invoice.status === 'refunded'
                    ? amountRefunded > 0 ? amountRefunded : invoice.total_amount
                    : invoice.status === 'paid'
                      ? invoice.total_amount
                      : outstanding > 0 ? outstanding : invoice.total_amount,
                )}
              </p>
              {invoice.status !== 'paid' && invoice.status !== 'refunded' && (
                <p className={cn('mt-0.5 text-xs', invoice.status === 'overdue' ? 'font-medium text-destructive' : 'text-muted-foreground')}>
                  Due {formatShortDate(invoice.due_date)}
                </p>
              )}
            </div>

            {invoice.dispute_state && (
              <div
                className={cn(
                  'rounded-lg border px-4 py-3 text-sm',
                  invoice.dispute_state === 'won'
                    ? 'border-border bg-muted/40 text-muted-foreground'
                    : 'border-destructive/30 bg-destructive/10 text-destructive',
                )}
              >
                <p className="font-semibold">
                  {invoice.dispute_state === 'open'
                    ? 'Payment disputed by the cardholder'
                    : invoice.dispute_state === 'lost'
                      ? 'Chargeback: the bank returned the money to the cardholder'
                      : 'Dispute won: the funds were released back to you'}
                </p>
                <p className="mt-0.5 text-xs opacity-90">
                  {formatMoney(Number(invoice.dispute_amount ?? 0))}
                  {invoice.dispute_reason ? ` · ${invoice.dispute_reason.replace(/_/g, ' ').toLowerCase()}` : ''}
                  {invoice.dispute_state === 'open' && invoice.dispute_due_at
                    ? ` · evidence due ${formatShortDate(invoice.dispute_due_at)}`
                    : ''}
                </p>
                {invoice.dispute_state === 'open' && (
                  <p className="mt-1 text-xs opacity-90">Respond with evidence in your Square dashboard under Disputes before the deadline.</p>
                )}
              </div>
            )}

            {/* Customer */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-foreground">
                <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                <CustomerLink
                  customerId={linkedCustomer?.id ?? invoice.customer_id}
                  name={invoice.customer_name}
                  className="font-medium"
                  onNavigate={() => onOpenChange(false)}
                />
                {invoice.customer?.company_name && (
                  <span className="text-sm text-muted-foreground">· {invoice.customer.company_name}</span>
                )}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
                {invoice.customer_phone && (
                  <a
                    href={`tel:${invoice.customer_phone}`}
                    className="flex items-center gap-1.5 text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Phone className="h-3.5 w-3.5" />
                    {invoice.customer_phone}
                  </a>
                )}
                {invoice.customer_email && (
                  <a
                    href={`mailto:${invoice.customer_email}`}
                    className="flex items-center gap-1.5 text-muted-foreground hover:text-primary"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Mail className="h-3.5 w-3.5" />
                    {invoice.customer_email}
                  </a>
                )}
              </div>
              {invoice.customer_address && (
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="text-muted-foreground">{invoice.customer_address}</span>
                </div>
              )}
              {invoice.service_address && invoice.service_address !== invoice.customer_address && (
                <div className="flex items-start gap-2 text-sm">
                  <Wrench className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="text-muted-foreground">Service at {invoice.service_address}</span>
                </div>
              )}
            </div>

            {/* Key facts */}
            <div className="grid grid-cols-1 gap-2 border-t pt-3 sm:grid-cols-2">
              <Fact icon={Calendar} label="Issued">{formatShortDate(invoice.issue_date)}</Fact>
              <Fact icon={Calendar} label="Due">
                <span className={invoice.status === 'overdue' ? 'font-medium text-destructive' : ''}>
                  {formatShortDate(invoice.due_date)}
                </span>
              </Fact>
              {invoice.job?.job_number && (
                <Fact icon={Briefcase} label="Job">
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => { onOpenChange(false); navigate(`/jobs/${invoice.job_id}`); }}
                  >
                    {invoice.job.job_number}
                  </button>
                </Fact>
              )}
              {(invoice.job?.job_date || invoice.job?.scheduled_date) && (
                <Fact icon={Clock} label="Service date">{formatShortDate(invoice.job.job_date ?? invoice.job.scheduled_date)}</Fact>
              )}
              {invoice.job?.assigned_tech?.name && (
                <Fact icon={Wrench} label="Technician">{invoice.job.assigned_tech.name}</Fact>
              )}
              {invoice.customer?.customer_number && (
                <Fact icon={Hash} label="Customer no.">{invoice.customer.customer_number}</Fact>
              )}
              {invoice.creator?.full_name && (
                <Fact icon={User} label="Prepared by">{invoice.creator.full_name}</Fact>
              )}
              {closedFromApp && (
                <Fact icon={Smartphone} label="Closed from">Steddy app (on site)</Fact>
              )}
              {invoice.signed_at && (
                <Fact icon={PenLine} label="Signed by">
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => setShowSignature(true)}
                  >
                    {invoice.signer_name || 'Customer'} on {formatShortDate(invoice.signed_at)}
                  </button>
                </Fact>
              )}
              {invoice.sent_at && (
                <Fact icon={Mail} label="Sent">{formatShortDate(invoice.sent_at)}</Fact>
              )}
              {invoice.status === 'paid' && invoice.paid_date && (
                <Fact icon={Landmark} label="Paid on">{formatShortDate(invoice.paid_date)}</Fact>
              )}
              {paymentUrl && (
                <Fact icon={Globe} label="Online payment">
                  <span className="capitalize">Square · {formatProviderStatus(invoice.provider_invoice_status)}</span>
                </Fact>
              )}
            </div>

            {/* Line items */}
            {lineItems.length > 0 && (
              <div className="border-t pt-3">
                <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  Line items
                </div>
                <div className="space-y-1.5 text-sm">
                  {lineItems.map((item, index) => (
                    <div key={index} className="flex justify-between gap-3">
                      <span className="min-w-0 flex-1 text-muted-foreground">
                        {item.description}
                        {item.quantity !== 1 && (
                          <span className="ml-1 text-xs tabular-nums">× {item.quantity}</span>
                        )}
                      </span>
                      <span className="shrink-0 tabular-nums">{formatMoney(item.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Financial summary */}
            <div className="space-y-1 border-t pt-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="tabular-nums">{formatMoney(invoice.subtotal)}</span>
              </div>
              {tax.enabled && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{formatTaxLine(tax)}</span>
                  <span className="tabular-nums">{formatMoney(invoice.gst_amount)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold">
                <span>Total</span>
                <span className="tabular-nums">{formatMoney(invoice.total_amount)}</span>
              </div>
              {invoice.amount_paid > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Paid</span>
                  <span className="tabular-nums text-success">− {formatMoney(invoice.amount_paid)}</span>
                </div>
              )}
              {amountRefunded > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Refunded</span>
                  <span className="tabular-nums text-destructive">− {formatMoney(amountRefunded)}</span>
                </div>
              )}
              {outstanding > 0 && invoice.amount_paid > 0 && (
                <div className="flex justify-between font-medium">
                  <span>Balance due</span>
                  <span className="tabular-nums text-destructive">{formatMoney(outstanding)}</span>
                </div>
              )}
            </div>

            {/* Terms */}
            {terms && (
              <div className="border-t pt-3 text-sm">
                <p className="mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">Payment terms</p>
                <p className="whitespace-pre-wrap text-muted-foreground">{terms}</p>
              </div>
            )}

            {/* Internal notes — staff only, never printed */}
            {invoice.notes && (
              <div className="rounded-lg bg-warning/10 px-3 py-2 text-sm">
                <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-warning">
                  <StickyNote className="h-3.5 w-3.5" />
                  Internal notes
                </p>
                <p className="whitespace-pre-wrap text-foreground/80">{invoice.notes}</p>
              </div>
            )}
          </div>
        )}
      </QuickViewSheetLayout>

      <EditInvoiceDialog invoiceId={invoiceId} open={showEdit} onOpenChange={setShowEdit} />
      <SendForPaymentDialog invoice={invoice ?? null} open={showSend} onOpenChange={setShowSend} />
      <EmailInvoiceDialog invoice={invoice ?? null} open={showEmail} onOpenChange={setShowEmail} />
      <InvoiceSignatureDialog invoice={invoice ?? null} open={showSignature} onOpenChange={setShowSignature} />
      <InvoiceActionConfirmDialog
        action={confirmAction}
        invoice={invoice ?? null}
        onOpenChange={(next) => !next && setConfirmAction(null)}
        onDone={(action) => {
          if (action === 'delete') onOpenChange(false);
        }}
      />
      <MarkInvoicePaidDialog
        invoice={invoice ?? null}
        open={showMarkPaid}
        onOpenChange={setShowMarkPaid}
      />

      {/* Hidden print target */}
      {invoice && (
        <div className="hidden print:block">
          <InvoicePreview ref={printRef} invoice={invoice} />
        </div>
      )}
    </>
  );
}
